import { ConnectedWallet, useWallets } from '@privy-io/react-auth';
import React from 'react';
import { Chain } from 'viem';
import { DEFAULT_CHAIN } from '../config';
import { Button, Flex } from '@mantine/core';
import { useWeb3Context } from '../providers/Web3Provider/useWeb3Context';
import { useMantineTheme, Title, Text } from '@mantine/core';

interface EthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

interface ProviderError {
  code?: number;
  message?: string;
}

interface WindowWithEthereum extends Window {
  ethereum?: EthereumProvider;
}

export const SwitchChain: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const { wallets }: { wallets: ConnectedWallet[] } = useWallets();
  const web3ProviderContext = useWeb3Context();
  const theme = useMantineTheme();

  const switchChain = async (chain: Chain) => {
    setLoading(true);

    if (!wallets || wallets.length === 0) {
      setLoading(false);
      return;
    }

    const wallet = wallets[0];
    let provider: EthereumProvider | null = null;
    if (typeof window !== 'undefined') {
      const windowWithEthereum = window as unknown as WindowWithEthereum;
      if (windowWithEthereum.ethereum) {
        provider = windowWithEthereum.ethereum;
      }
    }
    if (!provider && typeof wallet.getEthereumProvider === 'function') {
      provider = (await wallet.getEthereumProvider()) as EthereumProvider | null;
    }

    if (!provider || typeof provider.request !== 'function') {
      setLoading(false);
      return;
    }

    const chainIdHex = `0x${chain.id.toString(16)}`;
    const rpcUrls = Array.isArray(chain.rpcUrls.default.http)
      ? chain.rpcUrls.default.http
      : [chain.rpcUrls.default.http];

    try {
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainIdHex }],
        });
      } catch (switchError: unknown) {
        const error = switchError as ProviderError;
        // Error code 4902 means the chain is not added to MetaMask
        if (error?.code === 4902) {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: chainIdHex,
                chainName: chain.name,
                nativeCurrency: chain.nativeCurrency,
                rpcUrls: rpcUrls,
                blockExplorerUrls: chain.blockExplorers?.default?.url ? [chain.blockExplorers.default.url] : undefined,
              },
            ],
          });

          await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: chainIdHex }],
          });
        } else if (error?.code === 4001 || error?.message?.toLowerCase().includes('user rejected')) {
          setLoading(false);
          return;
        } else {
          throw switchError;
        }
      }

      // Update Privy's wallet state
      try {
        await wallet.switchChain(chain.id);
      } catch (privyError: unknown) {
        // Continue anyway since MetaMask is switched
      }
      // Wait for chain change to propagate, then reload
      await new Promise((resolve) => setTimeout(resolve, 1000));
      window.location.reload();
    } catch (error: unknown) {
      const providerError = error as ProviderError;
      if (providerError?.code !== 4001 && !providerError?.message?.toLowerCase().includes('user rejected')) {
        console.error('Error changing chain:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await web3ProviderContext.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Flex align="center" justify="center">
        <Flex
          mih="100vh"
          maw={theme.breakpoints.xs}
          direction="column"
          style={{ flexGrow: 1, overflowX: 'hidden' }}
          align="left"
          justify="top"
          bg="gray.9"
          px="1rem"
          gap="1rem"
        >
          <Title ta="center" mt="1rem" fz="3rem">
            Chain
            <Text inherit variant="gradient" component="span" gradient={{ from: 'pink', to: 'yellow' }}>
              &nbsp;not supported!
            </Text>
          </Title>
          <Text size="lg">
            <b>Oops! It seems your current network isn&apos;t supported yet.</b>
            <br />
            We&apos;re working hard to expand our chain compatibility and bring you a seamless experience across
            multiple networks.
            <br />
            In the meantime, you have a couple of options:
          </Text>
          <ul style={{ marginTop: '0.5rem', marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li>Switch to the default supported chain using the button below.</li>
            <li>Logout and explore other login options.</li>
          </ul>
          <Button onClick={() => switchChain(DEFAULT_CHAIN)} color="teal.9" loading={loading} fw="normal">
            Switch to default supported chain
          </Button>
          <Text align="center" mt="1rem">
            OR
          </Text>
          <Button onClick={() => handleLogout()} color="teal.9" variant="transparent" loading={loading} fw="normal">
            Logout
          </Button>
        </Flex>
      </Flex>
    </div>
  );
};
